import { createApi } from "@reduxjs/toolkit/query/react";

import { createAuthBaseQuery } from "../../common/utils";
import { HOSTNAME } from "../../constant/host";

/**
 * GitHub リポジトリ連携の API スライス（BE: github_integration、docs/api/github.md）。
 *
 * 管理（master_admin のみ。/console/github）:
 * - `GET    /api/admin/github/overview`               App の設定状況とインストール URL
 * - `GET    /api/admin/github/available-repositories` App のインストール先のリポジトリ
 * - `GET    /api/admin/github/repositories`           登録済みリポジトリ（設定込み）
 * - `POST   /api/admin/github/repositories`           登録
 * - `PATCH  /api/admin/github/repositories/:id`       紐付け・閲覧ポジション・ロール
 * - `DELETE /api/admin/github/repositories/:id`       登録解除
 * - `GET    /api/admin/github/repositories/:id/role-candidates?q=`
 * - `GET    /api/admin/github/repositories/:id/logs`  書き込みの記録
 *
 * プロジェクトのメンバー:
 * - `GET    /api/:project_id/github/repositories`     紐付け済みで閲覧できるもの
 *
 * リポジトリの閲覧・取り込み（viewer 以上。「リポジトリから最新を取得」用）:
 * - `GET    /api/github/repositories/:id/tree?path=&ref=&recursive=`
 * - `GET    /api/github/repositories/:id/commits?path=&ref=&limit=&page=`
 * - `GET    /api/github/repositories/:id/raw?path=&ref=`  テキスト全文（RPP 等）
 * - `POST   /api/github/repositories/:id/import`          {path, ref} → 転送開始
 * - `GET    /api/github/transfers/:id`                    自分の転送の状態
 */

/** API 表記のポジション（ADMIN はプロジェクト内管理者で常に書き込み可のため選べない） */
export type GitHubPosition =
  | "CLIENT"
  | "REVIEWER"
  | "PRODUCER"
  | "DIRECTOR"
  | "SPECIALIST";

export interface GitHubUserBrief {
  id: number;
  display_name: string;
  avatar: string | null;
}

export interface GitHubRoleCandidate extends GitHubUserBrief {
  positions: string[];
}

export interface GitHubRoleEntry {
  id: number;
  role: "editor";
  position: GitHubPosition | null;
  user: GitHubUserBrief | null;
}

export interface GitHubRepositorySummary {
  id: string;
  full_name: string;
  default_branch: string;
  is_private: boolean;
  html_url: string;
}

export interface GitHubRepositorySettings {
  projects: { id: string; name: string }[];
  allowed_positions: GitHubPosition[];
  allow_completed: boolean;
  roles: GitHubRoleEntry[];
}

export interface AdminGitHubRepository extends GitHubRepositorySummary {
  repo_id: number;
  installation_id: number;
  settings: GitHubRepositorySettings;
  created_at: string;
  updated_at: string;
}

export interface GitHubAvailableRepository {
  installation_id: number;
  account: string;
  repo_id: number;
  full_name: string;
  is_private: boolean;
  default_branch: string | null;
  /** 登録済みならその id */
  registered_repository_id: string | null;
}

export interface GitHubWriteLog {
  id: number;
  action: "create_branch" | "commit" | "create_pull_request";
  user: GitHubUserBrief | null;
  branch: string;
  commit_sha: string;
  url: string;
  detail: Record<string, unknown>;
  created_at: string;
}

export interface GitHubProjectRepository extends GitHubRepositorySummary {
  my_role: "viewer" | "editor" | "admin";
  can_edit: boolean;
}

export type GitHubRoleInput =
  | { role: "editor"; position: GitHubPosition }
  | { role: "editor"; user_id: number };

/** tree のエントリー（recursive=true は path 配下の全ファイル） */
export interface GitHubTreeEntry {
  path: string;
  type: "file" | "dir" | "submodule";
  size: number | null;
}

/** tree のページ情報（BE mcp_pagination.paginate と同形） */
export interface GitHubListPagination {
  total: number;
  offset: number;
  limit: number;
  returned: number;
  has_more: boolean;
  next_offset: number | null;
}

export interface GitHubCommitSummary {
  sha: string;
  message: string;
  author: string | null;
  author_login: string | null;
  /** ISO 8601（コミット日時。ファイル別の更新判定に使う） */
  date: string | null;
  html_url: string;
}

/** raw エンドポイントの応答（テキスト全文。バイナリ・10MB 超は 400） */
export interface GitHubRawFile {
  repository: GitHubRepositorySummary;
  ref: string;
  path: string;
  sha: string | null;
  size: number;
  lfs: boolean;
  content: string;
}

export interface GitHubTransfer {
  id: string;
  kind: "import" | "commit";
  status: "pending" | "running" | "succeeded" | "failed";
  repository: GitHubRepositorySummary;
  params: { path?: string; ref?: string };
  created_at: string | null;
  updated_at: string | null;
  /** status=succeeded のときのみ */
  result?: {
    file_key: string;
    file_name: string;
    size: number;
    lfs: boolean;
    path: string;
    ref: string;
    download_url?: string;
  };
  /** status=failed のときのみ */
  error?: string;
}

export interface GitHubRepositorySettingsInput {
  project_ids?: string[];
  allowed_positions?: GitHubPosition[];
  allow_completed?: boolean;
  roles?: GitHubRoleInput[];
}

interface Envelope<T> {
  status: string;
  data: T;
}

export const githubApi = createApi({
  reducerPath: "githubApi",
  baseQuery: createAuthBaseQuery(HOSTNAME),
  tagTypes: ["AdminGitHubRepositories", "AdminGitHubLogs", "ProjectGitHubRepositories"],
  endpoints: (builder) => ({
    getAdminGitHubOverview: builder.query<
      Envelope<{ configured: boolean; app_slug: string; install_url: string | null }>,
      void
    >({
      query: () => "/api/admin/github/overview",
    }),

    getAdminGitHubAvailableRepositories: builder.query<
      Envelope<{ repositories: GitHubAvailableRepository[] }>,
      void
    >({
      query: () => "/api/admin/github/available-repositories",
      providesTags: ["AdminGitHubRepositories"],
    }),

    getAdminGitHubRepositories: builder.query<
      Envelope<{ repositories: AdminGitHubRepository[] }>,
      void
    >({
      query: () => "/api/admin/github/repositories",
      providesTags: ["AdminGitHubRepositories"],
    }),

    createAdminGitHubRepository: builder.mutation<
      Envelope<{ repository: AdminGitHubRepository }>,
      GitHubRepositorySettingsInput & { repo_id: number; installation_id: number }
    >({
      query: (body) => ({ url: "/api/admin/github/repositories", method: "POST", body }),
      invalidatesTags: ["AdminGitHubRepositories", "ProjectGitHubRepositories"],
    }),

    updateAdminGitHubRepository: builder.mutation<
      Envelope<{ repository: AdminGitHubRepository }>,
      GitHubRepositorySettingsInput & { id: string }
    >({
      query: ({ id, ...body }) => ({
        url: `/api/admin/github/repositories/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["AdminGitHubRepositories", "ProjectGitHubRepositories"],
    }),

    deleteAdminGitHubRepository: builder.mutation<{ status: string }, string>({
      query: (id) => ({ url: `/api/admin/github/repositories/${id}`, method: "DELETE" }),
      invalidatesTags: ["AdminGitHubRepositories", "ProjectGitHubRepositories"],
    }),

    getAdminGitHubRoleCandidates: builder.query<
      Envelope<{ users: GitHubRoleCandidate[] }>,
      { id: string; q: string }
    >({
      query: ({ id, q }) => ({
        url: `/api/admin/github/repositories/${id}/role-candidates`,
        params: { q },
      }),
    }),

    getAdminGitHubWriteLogs: builder.query<Envelope<{ logs: GitHubWriteLog[] }>, string>({
      query: (id) => `/api/admin/github/repositories/${id}/logs`,
      providesTags: ["AdminGitHubLogs"],
    }),

    getProjectGitHubRepositories: builder.query<
      Envelope<{ repositories: GitHubProjectRepository[] }>,
      string
    >({
      query: (projectId) => `/api/${projectId}/github/repositories`,
      providesTags: ["ProjectGitHubRepositories"],
    }),

    // ── リポジトリの閲覧・取り込み（「リポジトリから最新を取得」ウィザード用）──
    // tree / commits / raw はウィザードから lazy trigger で逐次呼ぶ

    getGitHubTree: builder.query<
      Envelope<{
        repository: GitHubRepositorySummary;
        ref: string;
        path: string;
        entries: GitHubTreeEntry[];
        pagination: GitHubListPagination;
        note?: string;
      }>,
      {
        repositoryId: string;
        path?: string;
        ref?: string;
        recursive?: boolean;
        limit?: number;
        offset?: number;
      }
    >({
      query: ({ repositoryId, path, ref, recursive, limit, offset }) => ({
        url: `/api/github/repositories/${repositoryId}/tree`,
        params: {
          ...(path ? { path } : {}),
          ...(ref ? { ref } : {}),
          ...(recursive ? { recursive: "true" } : {}),
          ...(limit ? { limit } : {}),
          ...(offset ? { offset } : {}),
        },
      }),
    }),

    getGitHubCommits: builder.query<
      Envelope<{
        repository: GitHubRepositorySummary;
        ref: string;
        commits: GitHubCommitSummary[];
      }>,
      { repositoryId: string; path?: string; ref?: string; limit?: number }
    >({
      query: ({ repositoryId, path, ref, limit }) => ({
        url: `/api/github/repositories/${repositoryId}/commits`,
        params: {
          ...(path ? { path } : {}),
          ...(ref ? { ref } : {}),
          ...(limit ? { limit } : {}),
        },
      }),
    }),

    getGitHubRaw: builder.query<
      Envelope<GitHubRawFile>,
      { repositoryId: string; path: string; ref?: string }
    >({
      query: ({ repositoryId, path, ref }) => ({
        url: `/api/github/repositories/${repositoryId}/raw`,
        params: { path, ...(ref ? { ref } : {}) },
      }),
    }),

    importGitHubFile: builder.mutation<
      Envelope<{ transfer: GitHubTransfer }>,
      { repositoryId: string; path: string; ref?: string }
    >({
      query: ({ repositoryId, path, ref }) => ({
        url: `/api/github/repositories/${repositoryId}/import`,
        method: "POST",
        body: { path, ...(ref ? { ref } : {}) },
      }),
    }),

    getGitHubTransfer: builder.query<Envelope<{ transfer: GitHubTransfer }>, string>({
      query: (transferId) => `/api/github/transfers/${transferId}`,
    }),
  }),
});

export const {
  useGetAdminGitHubOverviewQuery,
  useGetAdminGitHubAvailableRepositoriesQuery,
  useGetAdminGitHubRepositoriesQuery,
  useCreateAdminGitHubRepositoryMutation,
  useUpdateAdminGitHubRepositoryMutation,
  useDeleteAdminGitHubRepositoryMutation,
  useGetAdminGitHubRoleCandidatesQuery,
  useGetAdminGitHubWriteLogsQuery,
  useGetProjectGitHubRepositoriesQuery,
  useLazyGetGitHubTreeQuery,
  useLazyGetGitHubCommitsQuery,
  useLazyGetGitHubRawQuery,
  useImportGitHubFileMutation,
  useLazyGetGitHubTransferQuery,
} = githubApi;
